import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import { useCreateCustomerMutation } from "../../store/services/customerApi";
import { toast } from "react-toastify";

const CreateCustomerModal = ({ handleClose, onCustomerCreated }) => {
  const [formData, setFormData] = useState({
    username: "",
    customer_name: "",
    country_code: "+91",
    customer_mobile: "",
    customer_add1: "",
    customer_add2: "",
    city: "",
    state: "",
    pin: "",
    customer_email: "",
  });

  const [errors, setErrors] = useState({});

  const [createCustomer, { isLoading }] = useCreateCustomerMutation();
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.username) newErrors.username = "Username is required";
    if (!formData.customer_name)
      newErrors.customer_name = "Customer Name is required";
    if (!formData.city) newErrors.city = "City is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.pin.match(/^\d{6}$/))
      newErrors.pin = "pin must be a 6-digit number";
    if (!formData.customer_email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      newErrors.customer_email = "Invalid email format";

    if (formData.country_code === "+91") {
      if (!/^\d{10}$/.test(formData.customer_mobile)) {
        newErrors.customer_mobile =
          "Indian mobile number must be exactly 10 digits";
      }
    } else {
      if (!/^\d{5,15}$/.test(formData.customer_mobile)) {
        newErrors.customer_mobile =
          "Mobile number must be between 5 to 15 digits";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    try {
      await createCustomer(formData).unwrap();
      toast.success("Customer created successfully");
      onCustomerCreated();
      handleClose();
    } catch (err) {
      console.error("Error creating customer:", err);
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          left: 40,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "1rem", pb: 0 }}>
        Create New Customer
      </DialogTitle>
      <DialogContent>
        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(255,255,255,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
            }}
          >
            <img src="/ZZ5H.gif" alt="Loading..." width="80" />
            <Typography variant="h6" style={{ marginTop: 20 }}>
              please wait...
            </Typography>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Left Section */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                variant="outlined"
                margin="normal"
                required
                error={!!errors.username}
                helperText={errors.username}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                inputProps={{ style: { fontSize: "0.7rem" } }}
              />
              <TextField
                fullWidth
                label="Customer Name"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleInputChange}
                variant="outlined"
                margin="normal"
                required
                error={!!errors.customer_name}
                helperText={errors.customer_name}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                inputProps={{ style: { fontSize: "0.7rem" } }}
              />
              {/* Country Code & Mobile Number */}
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={4}>
                  <TextField
                    select
                    fullWidth
                    label="Country Code"
                    name="country_code"
                    value={formData.country_code}
                    onChange={handleInputChange}
                    SelectProps={{ native: true }}
                    variant="outlined"
                    margin="normal"
                    required
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                    InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                    inputProps={{ style: { fontSize: "0.7rem" } }}
                  >
                    <option value="+91">India (+91)</option>
                    <option value="+1">USA (+1)</option>
                    <option value="+44">UK (+44)</option>
                    <option value="+61">Australia (+61)</option>
                    {/* Add more countries as needed */}
                  </TextField>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="Customer Mobile"
                    name="customer_mobile"
                    value={formData.customer_mobile}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                    required
                    error={!!errors.customer_mobile}
                    helperText={errors.customer_mobile}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                    InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                    inputProps={{ style: { fontSize: "0.7rem" } }}
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                label="Customer Email"
                name="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={handleInputChange}
                variant="outlined"
                margin="normal"
                required
                error={!!errors.customer_email}
                helperText={errors.customer_email}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                inputProps={{ style: { fontSize: "0.7rem" } }}
              />
            </Grid>

            {/* Right Section */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer Address 1"
                name="customer_add1"
                value={formData.customer_add1}
                onChange={handleInputChange}
                variant="outlined"
                margin="normal"
                required
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                inputProps={{ style: { fontSize: "0.7rem" } }}
              />
              <TextField
                fullWidth
                label="Customer Address 2"
                name="customer_add2"
                value={formData.customer_add2}
                onChange={handleInputChange}
                variant="outlined"
                margin="normal"
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                inputProps={{ style: { fontSize: "0.7rem" } }}
              />

              {/* City and State in the same row */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                    required
                    error={!!errors.city}
                    helperText={errors.city}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                    InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                    inputProps={{ style: { fontSize: "0.7rem" } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                    required
                    error={!!errors.state}
                    helperText={errors.state}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                    InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                    inputProps={{ style: { fontSize: "0.7rem" } }}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="pin Code"
                name="pin"
                value={formData.pin}
                onChange={handleInputChange}
                variant="outlined"
                margin="normal"
                required
                error={!!errors.pin}
                helperText={errors.pin}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                inputProps={{ style: { fontSize: "0.7rem" } }}
              />
            </Grid>
          </Grid>

          <DialogActions>
            <Button
              onClick={handleClose}
              color="secondary"
              variant="outlined"
              sx={{ fontSize: "0.7rem" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              sx={{ fontSize: "0.7rem" }}
            >
              Create Customer
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCustomerModal;
